import { useMemo } from 'react';

import type { GridColDef } from '@mui/x-data-grid-premium';
import type { Apollo } from 'src/gql/generated';
import type { UserSubset } from 'src/types';

import {
  ReminderAssigneesCell,
  ReminderDateCell,
  ReminderDocumentCell,
  ReminderRemindMeCell,
  ReminderSourceCell,
  ReminderTitleCell,
  ReminderTypeCell,
} from './cells';
import type { ColumnOptionSubset } from './useReportColumnMap';
import { dateValueGetter } from '../reports';

export interface ReminderListRow extends Pick<
  Apollo.Reminder,
  | '__typename'
  | 'completedAt'
  | 'createdAt'
  | 'dueDate'
  | 'id'
  | 'isAiGenerated'
  | 'notificationOffsets'
  | 'reviewStatus'
  | 'title'
  | 'type'
> {
  aiRationale: string | null;
  aiReminderAutomation:
    | (Pick<Apollo.AiReminderAutomation, 'id' | 'title'> & {
        report: Pick<Apollo.Report, 'id' | 'name'> & { columnOptions: ColumnOptionSubset[] };
      })
    | null;
  assignees: UserSubset[];
  document: {
    __typename: 'Document';
    id: string;
    latestVersion:
      | { __typename: 'DocumentVersion'; id: string }
      | {
          __typename: 'StaticFileVersion';
          fileLocation: { __typename: 'DocFile'; mimetype: string };
          id: string;
        };
    name: string;
    variant: Apollo.DocumentVariant;
  };
}

export function useRemindersColumnDefs() {
  const columnDefs: GridColDef<ReminderListRow>[] = useMemo(
    () => [
      {
        field: 'title',
        flex: 1.5,
        headerName: 'Title',
        renderCell: ReminderTitleCell,
        type: 'string',
      },
      {
        field: 'document',
        flex: 1.5,
        headerName: 'Document',
        renderCell: ReminderDocumentCell,
        sortable: false,
      },
      {
        field: 'assignees',
        flex: 0.7,
        headerName: 'Assignees',
        renderCell: ReminderAssigneesCell,
        sortable: false,
      },
      {
        field: 'type',
        flex: 0.7,
        headerName: 'Type',
        renderCell: ReminderTypeCell,
        type: 'string',
      },
      {
        field: 'dueDate',
        flex: 0.7,
        headerName: 'Due Date',
        renderCell: ReminderDateCell,
        type: 'date',
        valueGetter: dateValueGetter,
      },
      {
        field: 'notificationOffsets',
        flex: 0.7,
        headerName: 'Notifications',
        renderCell: ReminderRemindMeCell,
        sortable: false,
      },
      {
        field: 'aiReminderAutomation',
        flex: 0.7,
        headerName: 'Source',
        renderCell: ReminderSourceCell,
        sortable: false,
      },
    ],
    []
  );
  return { columnDefs };
}
